// vg-binding: P7.D-27
// vg-binding: P7.D-29
// vg-binding: P7.D-30
//
// apps/vendor/src/features/inventory/api.ts
//
// Phase 7 Wave 15 polish — TanStack-Query surface for vendor inventory.
// Mirrors features/orders/api.ts shape: envelope unwrap, typed errors,
// csrfFetch for mutations, refetchInterval for liveness on lists.

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { csrfFetch } from '../../lib/api/csrfFetch';

// ---------------------------------------------------------------------------
// Wire types — mirror BE shape (inventoryQueryService + transferService)
// ---------------------------------------------------------------------------

export type AdjustmentType = 'INCREASE' | 'DECREASE' | 'CORRECTION';

export type MovementType =
  | 'adjust_increase'
  | 'adjust_decrease'
  | 'transfer_in'
  | 'transfer_out'
  | 'reserve'
  | 'unreserve'
  | 'deduct_assigned'
  | 'restock_cancelled';

export interface StockRow {
  stock_id: string;
  variant_id: string;
  warehouse_id: string;
  product_name: string | null;
  /** R10 — first VendorProductVariant.image_urls entry, when present. */
  product_image: string | null;
  variant_label: string | null;
  variant_sku: string | null;
  warehouse_name: string | null;
  on_hand: number;
  reserved: number;
  available: number;
  low_stock_threshold: number;
  is_low_stock: boolean;
  is_out_of_stock: boolean;
  version: number;
  last_updated_at: number;
}

export interface MovementRow {
  movement_id: string;
  variant_id: string;
  warehouse_id: string;
  product_name: string | null;
  variant_label: string | null;
  variant_sku: string | null;
  warehouse_name: string | null;
  type: MovementType;
  quantity: number;
  reason: string | null;
  reference: string | null;
  actor_user_id: string | null;
  before: { on_hand: number; reserved: number };
  after: { on_hand: number; reserved: number };
  occurred_at: number;
}

export interface TransferRow {
  transfer_id: string;
  variant_id: string;
  from_warehouse_id: string;
  to_warehouse_id: string;
  product_name: string | null;
  variant_label: string | null;
  variant_sku: string | null;
  from_warehouse_name: string | null;
  to_warehouse_name: string | null;
  quantity: number;
  reason: string;
  actor_user_id: string;
  occurred_at: number;
}

interface PaginatedRows<T> {
  rows: T[];
  next_cursor: string | null;
  total?: number;
  page?: number;
  page_count?: number;
  limit?: number;
}

interface ApiOk<T> { ok: true; data: T }
interface ApiErr {
  ok: false;
  error: { code: string; message: string };
}

export class InventoryApiError extends Error {
  public readonly code: string;
  public readonly statusCode: number;
  constructor(code: string, message: string, statusCode: number) {
    super(message);
    this.name = 'InventoryApiError';
    this.code = code;
    this.statusCode = statusCode;
  }
}

async function unwrap<T>(res: Response): Promise<T> {
  const body = (await res.json().catch(() => null)) as ApiOk<T> | ApiErr | null;
  if (!body) {
    throw new InventoryApiError('NETWORK', 'Empty response', res.status);
  }
  if (!('ok' in body) || body.ok !== true) {
    const err = (body as ApiErr).error;
    throw new InventoryApiError(
      err?.code ?? 'UNKNOWN',
      err?.message ?? 'Request failed',
      res.status,
    );
  }
  return body.data;
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

const STOCK_KEY = ['vendor', 'inventory', 'stock'] as const;
const STOCK_DETAIL_KEY = ['vendor', 'inventory', 'stock', 'detail'] as const;
const MOVEMENTS_KEY = ['vendor', 'inventory', 'movements'] as const;
const TRANSFERS_KEY = ['vendor', 'inventory', 'transfers'] as const;

// ---------------------------------------------------------------------------
// Warehouses (used by transfer modal to populate the target dropdown)
// ---------------------------------------------------------------------------

export interface WarehouseRow {
  warehouse_id: string;
  name: string;
  address: string;
  country: string;
  is_active: boolean;
  priority: number;
  products_assigned: number;
}

const WAREHOUSES_KEY = ['vendor', 'inventory', 'warehouses'] as const;

export function useWarehousesList(): UseQueryResult<
  { rows: WarehouseRow[] },
  InventoryApiError
> {
  return useQuery<{ rows: WarehouseRow[] }, InventoryApiError>({
    queryKey: [...WAREHOUSES_KEY],
    queryFn: async () => {
      const res = await csrfFetch('/api/v1/vendor/warehouses', {
        method: 'GET',
        credentials: 'include',
      });
      return unwrap<{ rows: WarehouseRow[] }>(res);
    },
    staleTime: 60_000,
  });
}

// ---------------------------------------------------------------------------
// Stock list
// ---------------------------------------------------------------------------

export interface StockListParams {
  warehouse_id?: string;
  variant_id?: string;
  cursor?: string;
  page?: number;
  limit?: number;
  /** Free-text search — matches product name on the server. */
  q?: string;
}

// ---------------------------------------------------------------------------
// Single stock detail (dbg-r5 Bug C — /inventory/:stockId drilldown)
// ---------------------------------------------------------------------------
//
// MovementRow re-used from the listing definition above (single source of
// truth). The legacy UPPERCASE union (`INCREASE | DECREASE | TRANSFER_*`)
// was removed in favour of the lowercase `MovementType` union that mirrors
// the BE wire contract.

export interface StockDetailData {
  stock: StockRow;
  movements: MovementRow[];
  movement_total: number;
}

export function useStockDetail(
  stockId: string | undefined,
): UseQueryResult<StockDetailData, InventoryApiError> {
  return useQuery<StockDetailData, InventoryApiError>({
    queryKey: [...STOCK_DETAIL_KEY, stockId],
    enabled: Boolean(stockId),
    queryFn: async () => {
      const res = await csrfFetch(
        `/api/v1/vendor/inventory/stock/${stockId}`,
        { method: 'GET', credentials: 'include' },
      );
      return unwrap<StockDetailData>(res);
    },
    staleTime: 15_000,
  });
}

export function useStockList(
  params: StockListParams = {},
): UseQueryResult<PaginatedRows<StockRow>, InventoryApiError> {
  return useQuery<PaginatedRows<StockRow>, InventoryApiError>({
    queryKey: [...STOCK_KEY, params],
    queryFn: async () => {
      const qs = new URLSearchParams();
      if (params.warehouse_id) qs.set('warehouse_id', params.warehouse_id);
      if (params.variant_id) qs.set('variant_id', params.variant_id);
      if (params.cursor) qs.set('cursor', params.cursor);
      if (params.page) qs.set('page', String(params.page));
      if (params.limit) qs.set('limit', String(params.limit));
      if (params.q && params.q.trim().length > 0) qs.set('q', params.q.trim());
      const qstr = qs.toString();
      const res = await csrfFetch(
        `/api/v1/vendor/inventory/stock${qstr ? `?${qstr}` : ''}`,
        { method: 'GET', credentials: 'include' },
      );
      return unwrap<PaginatedRows<StockRow>>(res);
    },
    refetchInterval: 30 * 1000,
  });
}

// ---------------------------------------------------------------------------
// Movements list
// ---------------------------------------------------------------------------

export interface MovementsParams {
  type?: MovementType;
  variant_id?: string;
  warehouse_id?: string;
  from_ms?: number;
  to_ms?: number;
  cursor?: string;
  page?: number;
  limit?: number;
}

export function useMovements(
  params: MovementsParams = {},
): UseQueryResult<PaginatedRows<MovementRow>, InventoryApiError> {
  return useQuery<PaginatedRows<MovementRow>, InventoryApiError>({
    queryKey: [...MOVEMENTS_KEY, params],
    queryFn: async () => {
      const qs = buildMovementsQuery(params);
      const qstr = qs.toString();
      const res = await csrfFetch(
        `/api/v1/vendor/inventory/movements${qstr ? `?${qstr}` : ''}`,
        { method: 'GET', credentials: 'include' },
      );
      return unwrap<PaginatedRows<MovementRow>>(res);
    },
    refetchInterval: 30 * 1000,
  });
}

function buildMovementsQuery(params: MovementsParams): URLSearchParams {
  const qs = new URLSearchParams();
  if (params.type) qs.set('type', params.type);
  if (params.variant_id) qs.set('variant_id', params.variant_id);
  if (params.warehouse_id) qs.set('warehouse_id', params.warehouse_id);
  if (typeof params.from_ms === 'number') qs.set('from_ms', String(params.from_ms));
  if (typeof params.to_ms === 'number') qs.set('to_ms', String(params.to_ms));
  if (params.cursor) qs.set('cursor', params.cursor);
  if (params.page) qs.set('page', String(params.page));
  if (params.limit) qs.set('limit', String(params.limit));
  return qs;
}

/** Trigger CSV download for current movement filters. */
export async function downloadMovementsCsv(params: MovementsParams = {}): Promise<void> {
  const qs = buildMovementsQuery(params);
  const qstr = qs.toString();
  const res = await csrfFetch(
    `/api/v1/vendor/inventory/movements/export${qstr ? `?${qstr}` : ''}`,
    { method: 'GET', credentials: 'include' },
  );
  if (!res.ok) {
    throw new InventoryApiError('EXPORT_FAILED', 'CSV export failed', res.status);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `vendor-movements-${Date.now()}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// Transfers list
// ---------------------------------------------------------------------------

export interface TransfersParams {
  cursor?: string;
  page?: number;
  limit?: number;
}

export function useTransfers(
  params: TransfersParams = {},
): UseQueryResult<PaginatedRows<TransferRow>, InventoryApiError> {
  return useQuery<PaginatedRows<TransferRow>, InventoryApiError>({
    queryKey: [...TRANSFERS_KEY, params],
    queryFn: async () => {
      const qs = new URLSearchParams();
      if (params.cursor) qs.set('cursor', params.cursor);
      if (params.page) qs.set('page', String(params.page));
      if (params.limit) qs.set('limit', String(params.limit));
      const qstr = qs.toString();
      const res = await csrfFetch(
        `/api/v1/vendor/inventory/transfers${qstr ? `?${qstr}` : ''}`,
        { method: 'GET', credentials: 'include' },
      );
      return unwrap<PaginatedRows<TransferRow>>(res);
    },
    refetchInterval: 30 * 1000,
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export interface AdjustmentInput {
  variant_id: string;
  warehouse_id: string;
  type: AdjustmentType;
  quantity: number;
  reason: string;
}

export interface AdjustmentResult {
  applied: boolean;
  request_id: string | null;
  on_hand_after: number | null;
}

export function useCreateAdjustment(): UseMutationResult<
  AdjustmentResult,
  InventoryApiError,
  AdjustmentInput
> {
  const qc = useQueryClient();
  return useMutation<AdjustmentResult, InventoryApiError, AdjustmentInput>({
    mutationFn: async (input) => {
      const res = await csrfFetch('/api/v1/vendor/inventory/adjustments', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      });
      return unwrap<AdjustmentResult>(res);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: STOCK_KEY });
      void qc.invalidateQueries({ queryKey: MOVEMENTS_KEY });
    },
  });
}

export interface TransferInput {
  variant_id: string;
  from_warehouse_id: string;
  to_warehouse_id: string;
  quantity: number;
  reason: string;
}

export interface TransferResult {
  transfer_id: string;
  from_on_hand_after: number;
  to_on_hand_after: number;
}

export function useCreateTransfer(): UseMutationResult<
  TransferResult,
  InventoryApiError,
  TransferInput
> {
  const qc = useQueryClient();
  return useMutation<TransferResult, InventoryApiError, TransferInput>({
    mutationFn: async (input) => {
      const res = await csrfFetch('/api/v1/vendor/inventory/transfers', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      });
      return unwrap<TransferResult>(res);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: STOCK_KEY });
      void qc.invalidateQueries({ queryKey: MOVEMENTS_KEY });
      void qc.invalidateQueries({ queryKey: TRANSFERS_KEY });
    },
  });
}
